import { OptionModalProps } from ".";
import { useState, KeyboardEvent } from "react";
import { ListEditTable, ListEditTableDescription } from "./listEditor";
import listTableStyles from "./listEditor.module.css";
import { CoreButton, CoreModal, CorePaper, CoreTitleCard } from "../../../core";
import { useMobileLayout } from "../../../hooks";
import { ProctoringOption } from "@verity/proctoring-option/types";
import { TextField } from "@mui/material";
import i18nPackage from "../../../i18n/i18n";

const MODIFIER_KEYS = ["control", "ctrl", "meta", "shift", "alt", "command", "cmd", "super", "option", "altgr"];

export default function KeyboardShortcutBlockListModal<T extends Partial<ProctoringOption>>({
	onClose,
	currOption,
	updateOption,
}: OptionModalProps<T>) {
	const { keyboardShortcutBlockList } = currOption;
	const [shortcutBlockList, setShortcutBlockList] = useState(keyboardShortcutBlockList ?? []);
	const [shortcutText, setShortcutText] = useState("");
	const isMobileLayout = useMobileLayout();

	const updateShortcut = (event: KeyboardEvent) => {
		let { key } = event;
		const keys: string[] = [];
		if (event.ctrlKey) {
			keys.push("Ctrl");
		}
		if (event.metaKey) {
			keys.push("Meta");
		}
		if (event.shiftKey) {
			keys.push("Shift");
		}
		if (event.altKey) {
			keys.push("Alt");
		}
		if (!MODIFIER_KEYS.includes(key.toLowerCase())) {
			if (key === " ") {
				key = "Space";
			}
			keys.push(key.length > 1 ? key : key.toUpperCase());
		}
		setShortcutText(keys.join("+"));
	};

	const onSave = () => {
		const uniqueList = Array.from(new Set(shortcutBlockList));
		updateOption({
			...currOption,
			keyboardShortcutBlockList: uniqueList,
		});
		onClose();
	};

	const content = (
		<ListEditTable
			currList={shortcutBlockList}
			updateList={setShortcutBlockList}
			emptyText={i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.emptyText")}
		/>
	);

	return (
		<CoreModal
			open
			onClose={onClose}
			title={
				isMobileLayout
					? undefined
					: i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.title")
			}
			minWidth={isMobileLayout ? undefined : "min(65%, 700px)"}
			widthFixed
			heightFixed={isMobileLayout}
			fullScreen={isMobileLayout}
			showTopCloseButton={!isMobileLayout}
			dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.cancel"),
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: onClose,
				},
				{
					text: i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.save"),
					color: "primary",
					onClick: onSave,
				},
			]}
		>
			{isMobileLayout ? (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						width: "100%",
						gap: "10px",
						background: "f4f4f4",
					}}
				>
					<CoreTitleCard
						title={i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.title")}
						description={
							<>{i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.description")}</>
						}
						style={{ minWidth: "300px", padding: "15px 10px 10px 10px", marginTop: "10px" }}
						slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
					/>
					<CorePaper
						component="section"
						sx={{
							display: "flex",
							flexWrap: "wrap",
							justifyContent: "space-between",
							width: "100%",
							columnGap: "24px",
							padding: "10px",
							boxSizing: "border-box",
						}}
					>
						<div style={{ width: "100%", maxWidth: "100%" }}>
							<div className={listTableStyles.inputRow}>
								<TextField
									type="text"
									className={listTableStyles.inputField}
									value={shortcutText}
									autoComplete="off"
									placeholder={i18nPackage.t(
										"editQuizPage.configurationStep.keyboardShortcutBlockListModal.placeholder",
									)}
									onKeyDown={(event) => {
										updateShortcut(event);
									}}
								/>
								<CoreButton
									onClick={() => {
										if (shortcutText) {
											setShortcutBlockList(shortcutBlockList.concat([shortcutText]));
											setShortcutText("");
										}
									}}
								>
									{i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.add")}
								</CoreButton>
							</div>
							{content}
						</div>
					</CorePaper>
				</div>
			) : (
				<>
					<ListEditTableDescription>
						{i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.description")}
					</ListEditTableDescription>
					<div>
						<div className={listTableStyles.inputRow}>
							<TextField
								type="text"
								className={listTableStyles.inputField}
								value={shortcutText}
								autoComplete="off"
								placeholder={i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.placeholder")}
								onKeyDown={(event) => {
									updateShortcut(event);
								}}
							/>
							<CoreButton
								onClick={() => {
									if (shortcutText) {
										setShortcutBlockList(shortcutBlockList.concat([shortcutText]));
										setShortcutText("");
									}
								}}
							>
								{i18nPackage.t("editQuizPage.configurationStep.keyboardShortcutBlockListModal.add")}
							</CoreButton>
						</div>
						{content}
					</div>
				</>
			)}
		</CoreModal>
	);
}
